import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { MdClose, MdAdd, MdSearch, MdEdit } from 'react-icons/md';
import GraphConfigEditModal from './GraphConfigEditModal';
import { BiCheckCircle, BiBarChart, BiLineChart, BiPieChartAlt2, BiBarChartSquare, BiNetworkChart } from 'react-icons/bi';
import { Bs123 } from "react-icons/bs";
import { RegulationContext, LoaderContext } from '../../../App';
import DashGraphConfigService from '../../../core/service/DashGraphConfigService';
import { LS_AUTHORITY_KEY, GraphLayoutType } from '../../../core/constants/common';
import './NewWidgetLibraryPanel.scss';

// Fallback mock widget data organized by category and topic names


const NewWidgetLibraryPanel = ({ 
  category, 
  year, 
  onClose, 
  onAddWidget,
  // New props for embedded mode
  scope,
  layout,
  isEmbedded = false
}) => {
  const { t } = useTranslation();
  const { glbRegulation } = useContext(RegulationContext);
  const { setLoaderDisplay } = useContext(LoaderContext);

  const authority = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const companyId = authority?.profile?.company?.id;
  const standardId = glbRegulation?.id;

  const [widgets, setWidgets] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editWidget, setEditWidget] = useState(null);

  // Fetch widgets from API when component mounts or category changes
  useEffect(() => {
    if (companyId && standardId && category) {
      fetchWidgetsFromAPI();
    }
  }, [companyId, standardId, category, isEmbedded, scope, layout]);

  const fetchWidgetsFromAPI = async () => {
    if (!companyId || !standardId) return;

    setLoading(true);
    setError(null);

    try {
      // Convert category name to API format
      const apiCategory = category === 'Social'
        ? 'SOCIAL'
        : category === 'Governance'
          ? 'GOVERNANCE'
          : 'ENVIRONMENTAL';

      let response;
      
      // For embedded mode with scope/layout parameters, use them in the API call
      if (isEmbedded && scope && layout) {
        // Parse layout type from scope for the API call
        const graphLayoutType = parseLayoutTypeFromFilter(scope);
        response = await DashGraphConfigService.getCompanyGraphs(
          companyId,
          standardId,
          apiCategory,
          glbRegulation?.name,
          null,
          null,
          false, // fetchAll = false to get all widgets (active and inactive)
          true, // fetchAll = true to get all widgets
          graphLayoutType?.text,
          '',
          graphLayoutType?.value
        );
      } else {
        // Default API call for standalone mode
        response = await DashGraphConfigService.getCompanyGraphs(
          companyId,
          standardId,
          apiCategory,
          glbRegulation?.name,
          null,
          false,
          false, // fetchAll = false to get all widgets (active and inactive)
          true // fetchAll = true to get all widgets
        );
      }

      if (response.status === 200) {
        const graphs = response.data?.graphs || [];

        // Transform API data to match our widget structure
        const transformedWidgets = transformAPIWidgets(graphs, category);
        setWidgets(transformedWidgets);
      }
    } catch (error) {
      console.error('Error fetching widgets from API:', error);
      setError(t("errorLoadingWidgets.label"));
      // Fallback to mock data
      // setWidgets(mockWidgets);
    } finally {
      setLoading(false);
    }
  };

  const transformAPIWidgets = (graphs, category) => {
    // Group graphs by topic
    const groupedGraphs = graphs.reduce((groups, graph) => {
      const topic = graph.topicName || 'General';
      if (!groups[topic]) {
        groups[topic] = [];
      }

      // Transform graph to widget format
      const widget = {
        id: graph.id,
        title: graph.title || graph.name,
        description: graph.description || '',
        type: graph.type || 'LINE',
        category: category,
        topic: topic,
        active: graph.active || false,
        icon: getWidgetIcon(graph.type || 'LINE'),
        // Add API-specific data
        apiData: graph
      };

      groups[topic].push(widget);
      return groups;
    }, {});

    return { [category]: groupedGraphs };
  };

  const getWidgetIcon = (type) => {
    const icons = {
      'LINE': <BiLineChart />,
      'BAR': <BiBarChart />,
      'PIE': <BiPieChartAlt2 />,
      'STACKED_BAR': <BiBarChartSquare />,
      'NUMBER': <Bs123 />,
      'KPI': <BiNetworkChart />
    };
    return icons[type] || <BiBarChartSquare />;
  };

  // Parse layout type from filter scope (same as in other components)
  const parseLayoutTypeFromFilter = (f) => {
    console.log('NewWidgetLibraryPanel parseLayoutTypeFromFilter called with:', f);
    switch (f) {
      case 'All':
      case GraphLayoutType.OVERVIEW.name: 
        console.log('NewWidgetLibraryPanel matched OVERVIEW case');
        return GraphLayoutType.OVERVIEW;
      case GraphLayoutType.SCOPE_1.name: 
        console.log('NewWidgetLibraryPanel matched SCOPE_1 case');
        return GraphLayoutType.SCOPE_1;
      case GraphLayoutType.SCOPE_2.name: 
        console.log('NewWidgetLibraryPanel matched SCOPE_2 case');
        return GraphLayoutType.SCOPE_2;
      case GraphLayoutType.SCOPE_3.name: 
        console.log('NewWidgetLibraryPanel matched SCOPE_3 case');
        return GraphLayoutType.SCOPE_3;
      default: 
        console.log('NewWidgetLibraryPanel matched default case');
        return { name: '', value: '' };
    }
  };

  const handleWidgetToggle = async (widgetId) => {
    try {
      // Find the widget to get its current active status
      let currentWidget = null;

      Object.keys(widgets[category] || {}).forEach(topic => {
        const topicWidgets = widgets[category][topic];
        const widgetIndex = topicWidgets.findIndex(w => w.id === widgetId);
        if (widgetIndex !== -1) {
          currentWidget = topicWidgets[widgetIndex];
        }
      });

      if (!currentWidget) return;

      // Toggle the widget state (activate if inactive, deactivate if active)
      const newActiveState = !currentWidget.active;

      // Call API to activate/deactivate widget
      const response = await DashGraphConfigService.activateGraph(widgetId, newActiveState);

      if (response.status === 200) {
        // Update local state
        setWidgets(prevWidgets => {
          const updatedWidgets = { ...prevWidgets };
          const categoryWidgets = updatedWidgets[category];

          // Find the widget in the category's topics
          Object.keys(categoryWidgets).forEach(topic => {
            const topicWidgets = categoryWidgets[topic];
            const widgetIndex = topicWidgets.findIndex(w => w.id === widgetId);

            if (widgetIndex !== -1) {
              topicWidgets[widgetIndex] = {
                ...topicWidgets[widgetIndex],
                active: newActiveState
              };
            }
          });

          return updatedWidgets;
        });

        // Call onAdd to refresh the dashboard
        onAddWidget();
      }
    } catch (error) {
      console.error('Error toggling widget:', error);
      // Show error message to user
      setError(t("errorUpdatingWidget.label"));
    }
  };

  const getWidgetTypeColor = (type) => {
    const colors = {
      'LINE': '#1976d2',
      'BAR': '#2e7d32',
      'PIE': '#ed6c02',
      'STACKED_BAR': '#9c27b0',
      'NUMBER': '#d32f2f'
    };
    return colors[type] || '#666';
  };

  const getWidgetTypeLabel = (type) => {
    const labels = {
      'LINE': t("NewWidgetLibraryPanel.widgetType.line.label"),
      'BAR': t("NewWidgetLibraryPanel.widgetType.bar.label"),
      'PIE': t("NewWidgetLibraryPanel.widgetType.pie.label"),
      'STACKED_BAR': t("NewWidgetLibraryPanel.widgetType.stacked.label"),
      'NUMBER': t("NewWidgetLibraryPanel.widgetType.number.label")
    };
    return labels[type] || type;
  };

  const currentCategoryWidgets = widgets[category] || {};
  const topicNames = Object.keys(currentCategoryWidgets);

  // Filter widgets by search term
  const filteredTopics = {};
  topicNames.forEach(topic => {
    const topicWidgets = currentCategoryWidgets[topic].filter(widget =>
      widget.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      widget.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      topic.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (topicWidgets.length > 0) {
      filteredTopics[topic] = topicWidgets;
    }
  });

  // The panel list is fetched with fetchAll=true (summary); the full config incl. dataPoints/equations needs fetchAll=false
  const handleEditClick = async (widgetId, isActive = false) => {
    try {
      const apiCategory = category === 'Social'
        ? 'SOCIAL'
        : category === 'Governance'
          ? 'GOVERNANCE'
          : 'ENVIRONMENTAL';
      setLoaderDisplay(true);
      const response = await DashGraphConfigService.getCompanyGraphs(
        companyId,
        standardId,
        apiCategory,
        glbRegulation?.name,
        null,
        false,
        isActive, // active
        false  // fetchAll
      );
      const fullConfig = (response.data?.graphs || []).find(g => g.id === widgetId);
      if (fullConfig) setEditWidget(fullConfig);
    } catch (err) {
      console.error('Error fetching widget config:', err);
    } finally {
      setLoaderDisplay(false);
    }
  };

  const handleEditSaved = () => {
    setEditWidget(null);
    fetchWidgetsFromAPI();
    onAddWidget();
  };

  // Count total active widgets
  const activeWidgetsCount = Object.values(currentCategoryWidgets)
    .flat()
    .filter(w => w.active).length;

  return (
    <div className="new-widget-sidebar">
      {/* Header */}
      <div className="new-widget-sidebar__header">
        <div className="new-widget-sidebar__title">
          <h3>{category} {t("NewWidgetLibraryPanel.title.label")}</h3>
          <p>{activeWidgetsCount} {t("NewWidgetLibraryPanel.selected.label")}</p>
        </div>
        <button
          className="new-widget-sidebar__close-btn"
          onClick={onClose}
        >
          <MdClose />
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="new-widget-sidebar__loading">
          <div className="loading-spinner"></div>
          <p>{t("loadingWidgets.label")}</p>
        </div>
      )}

      {/* {error && (
        <div className="new-widget-sidebar__error">
          <p>{error}</p>
          <button onClick={fetchWidgetsFromAPI}>{t("retry.label")}</button>
        </div>
      )} */}

      {/* Search */}
      <div className="new-widget-sidebar__search">
        <div className="new-widget-sidebar__search-input">
          <MdSearch className="new-widget-sidebar__search-icon" />
          <input
            type="text"
            placeholder={t("NewWidgetLibraryPanel.searchPlaceholder.label")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Widgets by Topic */}
      <div className="new-widget-sidebar__content">
        <div className="new-widget-sidebar__content-title">
          {t("WidgetLibPanelSubheading.label")}
        </div>
        {!loading && !error && Object.keys(filteredTopics).length > 0 ? (
          <div className="new-widget-sidebar__topics">
            {Object.entries(filteredTopics).map(([topicName, topicWidgets]) => (
              <div key={topicName} className="new-widget-sidebar__topic">
                <h4 className="new-widget-sidebar__topic-title">{topicName}</h4>
                <div className="new-widget-sidebar__widgets-list">
                  {topicWidgets.map(widget => (
                    <div
                      key={widget.id}
                      className={`new-widget-sidebar__widget-item ${widget.active ? 'new-widget-sidebar__widget-item--active' : ''
                        }`}
                      onClick={() => handleWidgetToggle(widget.id)}
                    >
                      <div className="new-widget-sidebar__widget-icon">
                        {widget.icon}
                      </div>
                      <div className="new-widget-sidebar__widget-info">
                        <h4 className="new-widget-sidebar__widget-title">
                          {widget.title}
                        </h4>
                        <p className="new-widget-sidebar__widget-description">
                          {widget.description}
                        </p>
                        <div className="new-widget-sidebar__widget-meta">
                          <span
                            className="new-widget-sidebar__widget-type"
                            style={{ backgroundColor: getWidgetTypeColor(widget.type) }}
                          >
                            {getWidgetTypeLabel(widget.type)}
                          </span>
                          {widget.active && (
                            <BiCheckCircle className="new-widget-sidebar__widget-status" />
                          )}
                          <MdEdit
                            className="new-widget-sidebar__widget-edit ms-2"
                            title={t("Edit.label")}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(widget.id, widget.active);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="new-widget-sidebar__empty">
            <div className="new-widget-sidebar__empty-icon">🔍</div>
            <h4>{t("NewWidgetLibraryPanel.noWidgetsFound.label")}</h4>
            <p>{t("NewWidgetLibraryPanel.adjustSearchTerms.label")}</p>
          </div>
        )}
      </div>

      <GraphConfigEditModal
        show={!!editWidget}
        graphConfig={editWidget}
        standardId={standardId}
        onClose={() => setEditWidget(null)}
        onSaved={handleEditSaved}
      />
    </div>
  );
};

export default NewWidgetLibraryPanel; 